import { Box, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { CustomTable } from '../../common/CustomTable';
import DownloadIcon from '@mui/icons-material/Download';

interface SmsOverviewTabProps {
  metrics: any;
  comparison?: any;
  formatWithComparison: (value: string | number, prevValue?: string | number) => React.ReactNode;
  renderMetricCard: (title: string, value: string | number, comparisonValue?: number, rawValue?: number, inverted?: boolean, formatter?: (val: number) => string) => React.ReactNode;
  currentStartDate: string;
  currentEndDate: string;
  previousStartDate: string | null;
  previousEndDate: string | null;
}

export const SmsOverviewTab = ({
  metrics,
  comparison,
  formatWithComparison,
  renderMetricCard,
  currentStartDate,
  currentEndDate,
  previousStartDate,
  previousEndDate,
}: SmsOverviewTabProps) => {
  const handleExportKeyMetrics = async () => {
    const { metricsToCSV, downloadZipFile, formatDateForFilename } = await import('../../../utils/exportUtils');

    const keyMetrics = [
      { label: 'Total Messages', value: metrics.totalMessages, previousValue: comparison?.totalMessages },
      { label: 'Inbound', value: metrics.inboundMessages, previousValue: comparison?.inboundMessages },
      { label: 'Outbound', value: metrics.outboundMessages, previousValue: comparison?.outboundMessages },
      { label: 'Avg Length (chars)', value: metrics.averageLength, previousValue: comparison?.averageLength },
      { label: 'Max Length (chars)', value: metrics.maxLength, previousValue: comparison?.maxLength },
      { label: 'Min Length (chars)', value: metrics.minLength, previousValue: comparison?.minLength },
    ];

    const dateStr = formatDateForFilename();
    const csvContent = metricsToCSV(keyMetrics, currentStartDate, currentEndDate, previousStartDate ?? undefined, previousEndDate ?? undefined);

    await downloadZipFile(
      [{ filename: `${dateStr}-sms-key-metrics.csv`, content: csvContent }],
      `${dateStr}-sms-key-metrics.zip`,
    );
  };

  return (
    <>
      {/* Key Metrics Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 2 }}>
        <Typography variant='h6'>🎯 Key Metrics</Typography>
        <Tooltip title='Export key metrics'>
          <IconButton size='small' onClick={handleExportKeyMetrics} sx={{ color: '#1a667a' }}>
            <DownloadIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Total Messages',
            metrics.totalMessages,
            comparison?.totalMessages,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Inbound',
            metrics.inboundMessages,
            comparison?.inboundMessages,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Outbound',
            metrics.outboundMessages,
            comparison?.outboundMessages,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Avg Length',
            `${metrics.averageLength} chars`,
            comparison?.averageLength,
            metrics.averageLength,
            false,
            (val) => `${val} chars`,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Max Length',
            `${metrics.maxLength} chars`,
            comparison?.maxLength,
            metrics.maxLength,
            false,
            (val) => `${val} chars`,
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderMetricCard(
            'Min Length',
            `${metrics.minLength} chars`,
            comparison?.minLength,
            metrics.minLength,
            false,
            (val) => `${val} chars`,
          )}
        </Grid>
      </Grid>

      {/* Tables Section */}
      <Grid container spacing={3}>
      {/* By Delivery Status */}
      {metrics.messagesByStatus && metrics.messagesByStatus.length > 0 && (
        <Grid item xs={12} md={6}>
          <CustomTable
            title='By Delivery Status'
            columns={[
              { key: 'status', label: 'Status' },
              { key: 'count', label: 'Count' },
              { key: 'inbound', label: 'Inbound' },
              { key: 'outbound', label: 'Outbound' },
              { key: 'avgLength', label: 'Avg Length' },
            ]}
            rows={metrics.messagesByStatus.map((s: any) => {
              const prevData = comparison?.messagesByStatus?.find(
                (prev: any) => prev.status === s.status,
              );
              const prevCount = prevData?.count ?? 0;
              const prevInbound = prevData?.inboundCount ?? 0;
              const prevOutbound = prevData?.outboundCount ?? 0;
              const prevAvgLength = prevData?.averageLength ?? 0;

              return {
                status: s.status,
                count: formatWithComparison(s.count, prevCount),
                inbound: formatWithComparison(s.inboundCount, prevInbound),
                outbound: formatWithComparison(s.outboundCount, prevOutbound),
                avgLength: formatWithComparison(
                  `${s.averageLength} chars`,
                  `${prevAvgLength} chars`,
                ),
              };
            })}
            defaultText='No data available'
            loading={false}
            exportable={true}
            exportFilename='sms-by-status.csv'
          />
        </Grid>
      )}

      {/* By Tracking Number */}
      {metrics.messagesByTrackingNumber && metrics.messagesByTrackingNumber.length > 0 && (
        <Grid item xs={12} md={6}>
          <CustomTable
            title='By Tracking Number'
            columns={[
              { key: 'label', label: 'Number Label' },
              { key: 'count', label: 'Count' },
              { key: 'inbound', label: 'Inbound' },
              { key: 'outbound', label: 'Outbound' },
              { key: 'avgLength', label: 'Avg Length' },
            ]}
            rows={metrics.messagesByTrackingNumber.slice(0, 10).map((tn: any) => {
              const prevData = comparison?.messagesByTrackingNumber?.find(
                (prev: any) => prev.trackingNumber === tn.trackingNumber,
              );
              const prevCount = prevData?.count ?? 0;
              const prevInbound = prevData?.inboundCount ?? 0;
              const prevOutbound = prevData?.outboundCount ?? 0;
              const prevAvgLength = prevData?.averageLength ?? 0;

              return {
                label: tn.label,
                count: formatWithComparison(tn.count, prevCount),
                inbound: formatWithComparison(tn.inboundCount, prevInbound),
                outbound: formatWithComparison(tn.outboundCount, prevOutbound),
                avgLength: formatWithComparison(
                  `${tn.averageLength} chars`,
                  `${prevAvgLength} chars`,
                ),
              };
            })}
            defaultText='No data available'
            loading={false}
            exportable={true}
            exportFilename='sms-by-tracking-number.csv'
          />
        </Grid>
      )}

      {/* By Message Category */}
      {metrics.messagesByCategory && metrics.messagesByCategory.length > 0 && (
        <Grid item xs={12}>
          <CustomTable
            title='By Message Category'
            columns={[
              { key: 'category', label: 'Category' },
              { key: 'count', label: 'Count' },
              { key: 'inbound', label: 'Inbound' },
              { key: 'outbound', label: 'Outbound' },
              { key: 'avgLength', label: 'Avg Length' },
              { key: 'maxLength', label: 'Max Length' },
              { key: 'minLength', label: 'Min Length' },
            ]}
            rows={metrics.messagesByCategory.map((c: any) => {
              const prevData = comparison?.messagesByCategory?.find(
                (prev: any) => prev.category === c.category,
              );
              const prevCount = prevData?.count ?? 0;
              const prevInbound = prevData?.inboundCount ?? 0;
              const prevOutbound = prevData?.outboundCount ?? 0;
              const prevAvgLength = prevData?.averageLength ?? 0;
              const prevMaxLength = prevData?.maxLength ?? 0;
              const prevMinLength = prevData?.minLength ?? 0;

              return {
                category: c.category,
                count: formatWithComparison(c.count, prevCount),
                inbound: formatWithComparison(c.inboundCount, prevInbound),
                outbound: formatWithComparison(c.outboundCount, prevOutbound),
                avgLength: formatWithComparison(
                  `${c.averageLength} chars`,
                  `${prevAvgLength} chars`,
                ),
                maxLength: formatWithComparison(
                  `${c.maxLength} chars`,
                  `${prevMaxLength} chars`,
                ),
                minLength: formatWithComparison(
                  `${c.minLength} chars`,
                  `${prevMinLength} chars`,
                ),
              };
            })}
            defaultText='No data available'
            loading={false}
            exportable={true}
            exportFilename='sms-by-category.csv'
          />
        </Grid>
      )}
      </Grid>
    </>
  );
};
